import { useCallback, useEffect, useRef, useState } from 'react';
import { ArtesDoPost } from '../componentes/ArtesDoPost.tsx';
import { avisar } from '../componentes/Avisos.tsx';
import { ConteudoDoPost } from '../componentes/ConteudoDoPost.tsx';
import { EscadaDoPost } from '../componentes/EscadaDoPost.tsx';
import { FormularioPost } from '../componentes/FormularioPost.tsx';
import { FundosDoPost } from '../componentes/FundosDoPost.tsx';
import { GuardaDoFormulario } from '../componentes/GuardaDoFormulario.tsx';
import { Icone } from '../componentes/Icone.tsx';
import { ErroDaApi, apiMarketing, type ConteudoDoPost as Conteudo, type PostDeMarketing } from '../lib/api.ts';
import { porExtenso } from '../lib/datas.ts';
import { guardarSaida } from '../lib/rota.ts';
import { nomeDaRede, nomeDaSituacao, nomeDoFormato } from '../lib/posts.ts';
import '../estilos/sistema.css';
import '../estilos/marketing.css';

/**
 * ⭐⭐ A FICHA DO POST, em página própria — a parte 2 da aba Marketing,
 * 15/10/2026 (PRD §50.5).
 *
 * Até a parte 1, o post abria numa janela com o plano e a escada. O conteúdo
 * (os slides, a legenda, os textos de cada rede) não cabe numa janela, e a
 * ficha vai crescer: os fundos (parte 3) e a montagem (parte 4) moram aqui
 * também. É o mesmo passo que a ficha da empresa do funil deu em 20/09/2026.
 *
 * - **O plano** (tema, data, redes, formato, observação) continua na janela de
 *   sempre, pelo botão "Editar o plano".
 * - **A escada** mora aqui, e aprovar com achado grave pergunta antes.
 * - **O conteúdo** é a parte 2.
 * - ⭐ **Os fundos** são a parte 3 (PRD §50.6): os prompts para o ChatGPT e o
 *   envio das imagens, uma por slide e a do fecho.
 * - ⭐ **As artes** são a parte 4 (PRD §50.7): a montagem sobre os fundos, a
 *   prévia e os arquivos para baixar.
 *
 * ⚠️ **Sair com texto por gravar pergunta antes**: o "voltar", o menu e o
 * fechar da aba. ⛔⛔ **Fala só com `/api/marketing`** (PRD §7.1).
 */
export function FichaDoPost({
  id,
  aoVoltar,
  aoNavegar,
  aoExpirarSessao,
}: {
  id: string;
  aoVoltar: () => void;
  /** O `ir` da casa: a guarda de saída chama de novo depois do "descartar". */
  aoNavegar: (caminho: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [post, setPost] = useState<PostDeMarketing | null>(null);
  const [conteudo, setConteudo] = useState<Conteudo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [pendente, setPendente] = useState(false);
  /** Para onde se ia quando a guarda segurou a saída. */
  const [saida, setSaida] = useState<string | null>(null);
  /** Sobe a cada releitura completa: o bloco do conteúdo remonta com o gravado. */
  const [geracao, setGeracao] = useState(0);
  /** ⭐ Parte 4: sobe a cada fundo que entra, troca ou sai; a seção das artes relê. */
  const [versaoDosFundos, setVersaoDosFundos] = useState(0);
  const fundosMudaram = useCallback(() => setVersaoDosFundos((v) => v + 1), []);

  const carregar = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([apiMarketing.ver(id), apiMarketing.conteudo(id)]);
      setPost(p);
      setConteudo(c);
      setErro(null);
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro(e instanceof ErroDaApi && e.status === 404 ? 'Este post não está mais no calendário.' : 'Não consegui carregar o post.');
    }
  }, [id, aoExpirarSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /* ⭐ Com texto por gravar, o menu pergunta antes de trocar de página. */
  const soltarGuarda = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!pendente) return;
    const soltar = guardarSaida((destino) => {
      setSaida(destino);
      return true;
    });
    soltarGuarda.current = soltar;
    return () => {
      soltar();
      soltarGuarda.current = null;
    };
  }, [pendente]);
  /** Sair de verdade: a guarda sai antes, para o próprio "ir" não ser segurado. */
  const sairPara = (destino: string) => {
    soltarGuarda.current?.();
    aoNavegar(destino);
  };

  if (erro) {
    return (
      <div className="mkt-ficha">
        <button type="button" className="mkt-voltar" onClick={aoVoltar}>
          <Icone nome="voltar" tamanho={14} /> Marketing
        </button>
        <p className="mkt-vazio">{erro}</p>
      </div>
    );
  }
  if (!post || !conteudo) return <div className="carregando">Carregando o post…</div>;

  const graves = conteudo.achados.filter((a) => a.gravidade === 'grave');

  return (
    <div className="mkt-ficha">
      <button type="button" className="mkt-voltar" onClick={() => (pendente ? setSaida('/marketing') : aoVoltar())}>
        <Icone nome="voltar" tamanho={14} /> Marketing
      </button>

      <section className="s-folha s-identidade mkt-ficha-identidade">
        <div className="s-monograma mkt-monograma" aria-hidden="true">
          <Icone nome="marketing" tamanho={28} />
        </div>
        <div className="mkt-identidade-texto">
          <span className="s-sobrelinha">Post · {nomeDoFormato(post.formato)}</span>
          <h1 className="titulo mkt-ficha-tema">{post.titulo}</h1>
          <p className="mkt-ficha-meta">
            <span>
              {post.publicadoEm ? 'Foi ao ar em ' : 'Previsto para '}
              {porExtenso(post.publicadoEm ?? post.dataPrevista)}
            </span>
            <span className="mkt-redes">
              {post.redes.map((r) => (
                <span key={r} className="mkt-rede-icone">
                  <Icone nome={r} tamanho={14} /> {nomeDaRede(r)}
                </span>
              ))}
            </span>
          </p>
          {post.observacao && <p className="mkt-ficha-obs">{post.observacao}</p>}
        </div>
        <div className="mkt-ficha-lado">
          <span className={`mkt-chip mkt-sit-${post.situacao}`}>
            <i aria-hidden="true" />
            {nomeDaSituacao(post.situacao)}
          </span>
          <button type="button" className="imv-bt" onClick={() => setEditando(true)}>
            <Icone nome="lapis" tamanho={14} /> Editar o plano
          </button>
        </div>
      </section>

      <EscadaDoPost
        post={post}
        hoje={conteudo.hoje}
        conteudoPorGravar={pendente}
        graves={graves}
        aoMudar={async (situacao, publicacao, aprovarComAchados) => {
          const p = await apiMarketing.mudarSituacao(post.id, situacao, publicacao, aprovarComAchados);
          setPost(p);
          avisar(`Agora o post está em ${nomeDaSituacao(p.situacao).toLowerCase()}.`);
          return p;
        }}
      />

      <ConteudoDoPost
        key={geracao}
        post={post}
        conteudo={conteudo}
        aoGravado={setConteudo}
        aoPendente={setPendente}
        aoRecarregar={() => {
          void carregar().then(() => setGeracao((g) => g + 1));
        }}
      />

      <FundosDoPost post={post} versaoDoConteudo={conteudo.versao} conteudoPorGravar={pendente} aoExpirarSessao={aoExpirarSessao} aoMudar={fundosMudaram} />

      <ArtesDoPost
        post={post}
        versaoDoConteudo={conteudo.versao}
        versaoDosFundos={versaoDosFundos}
        conteudoPorGravar={pendente}
        aoExpirarSessao={aoExpirarSessao}
      />

      {editando && (
        <FormularioPost
          inicial={post}
          dataSugerida={post.dataPrevista}
          aoFechar={() => setEditando(false)}
          aoSalvar={async (dados) => {
            const p = await apiMarketing.editar(post.id, dados);
            setPost(p);
            /* ⚠️ O formato e as redes mudam o fecho e a conferência do gravado; o rascunho fica. */
            setConteudo(await apiMarketing.conteudo(post.id));
            setEditando(false);
            avisar('Plano do post atualizado.');
          }}
          aoApagar={async () => {
            await apiMarketing.apagar(post.id);
            setEditando(false);
            avisar('Post apagado do calendário, com o conteúdo, os fundos e as artes dele.');
            sairPara('/marketing');
          }}
        />
      )}

      <GuardaDoFormulario
        pendencia={saida ? 'descartar' : null}
        oQue="o conteúdo do post"
        aoCancelar={() => setSaida(null)}
        aoSalvar={() => setSaida(null)}
        aoDescartar={() => {
          const destino = saida;
          setSaida(null);
          if (destino) sairPara(destino);
        }}
      />
    </div>
  );
}
